import { AnalyticsBrowser } from "@segment/analytics-next"
import type { Plugin, Analytics } from "@segment/analytics-next"

import {
  fetchCartId,
  isConsolidatedProduct,
  localStorageAvailable,
} from "./shop"

let analytics: Analytics | null = null

interface VisitedPage {
  sku: string
  first_visit_date: string
  last_visit_date: string
  number_of_visits: number
  url: string
}
interface PageEvent {
  type: "page"
  path: string
  url: string
}
interface TrackEvent {
  type: "track"
  event: string
  data: Record<string, any>
}

interface MissedEvent {
  event: PageEvent | TrackEvent
}

let missed_events: MissedEvent[] = []

if (
  process.env.GATSBY_INCLUDE_THIRD_PARTY_SCRIPTS === "1" &&
  process.env.GATSBY_SEGMENT_WRITE_KEY &&
  typeof window !== "undefined"
) {
  AnalyticsBrowser.load({
    writeKey: process.env.GATSBY_SEGMENT_WRITE_KEY,
  }).then(([response]) => {
    analytics = response
    window.analytics = response
    analytics.register(visitedProductPages)
    analytics.register(socialCookies)
    analytics.register(traits)

    trackMissedEvents()
  })
}

export const trackMissedEvents = () => {
  if (missed_events.length === 0) return

  const queued_events = missed_events
  missed_events = []

  queued_events.forEach(({ event }) => {
    if (event.type === "page") {
      trackSegmentPageEvent(event.path, event.url)
    } else if (event.type === "track") {
      trackSegmentEvent(event.event, event.data)
    }
  })
}

export const fetchVisitedPages = (): VisitedPage[] => {
  if (localStorageAvailable()) {
    const visitedPages = localStorage.getItem("visitedPages")
    return visitedPages ? JSON.parse(visitedPages) : []
  }

  return []
}

export const setVisitedPages = (pages: VisitedPage[]): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("visitedPages", JSON.stringify(pages))
  }
}

export const visitedProductPages: Plugin = {
  name: "store productVisitedPages to cookies",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    if (ctx.event?.event === "Product Viewed") {
      const visitedPages = fetchVisitedPages()

      const alreadyVisitedPage = visitedPages.find(
        (page) => page.url === ctx.event?.context?.page?.path
      )

      if (alreadyVisitedPage) {
        alreadyVisitedPage.last_visit_date = new Date().toISOString()
        alreadyVisitedPage.number_of_visits += 1
        setVisitedPages(visitedPages)
      } else {
        const newPage = {
          sku: ctx.event?.properties?.sku,
          first_visit_date: new Date().toISOString(),
          last_visit_date: new Date().toISOString(),
          number_of_visits: 1,
          url: ctx.event?.context?.page?.path,
        } as VisitedPage
        setVisitedPages([...visitedPages, newPage])
      }
    }
    return ctx
  },
}

export const socialCookies: Plugin = {
  name: "Add social cookie values to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },

  page: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },
}

const fetchUserIpAddress = async (): Promise<string | null> => {
  try {
    const wauCookie = getCookie("wau")
    if (!wauCookie) {
      return null
    }

    const response = await fetch(
      `${process.env.GATSBY_SHOP_API_URL}/signup/hello.json`
    )
    if (!response.ok) {
      console.warn(
        `Failed to fetch IP address: ${response.status} ${response.statusText}`
      )
      return null
    }

    const data = await response.json()
    return data.ip_address || null
  } catch (error) {
    console.warn("Error fetching IP address:", error)
    return null
  }
}

export const traits: Plugin = {
  name: "Add Traits to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
        }

        const visitedPages = fetchVisitedPages()
        if (visitedPages.length > 0 && ctx.event.context!.traits) {
          ctx.event.context!.traits.visited_pages = visitedPages
        }
      }
    }

    return ctx
  },

  page: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
        }

        const visitedPages = fetchVisitedPages()
        if (visitedPages.length > 0 && ctx.event.context!.traits) {
          ctx.event.context!.traits.visited_pages = visitedPages
        }
      }
    }
    return ctx
  },
}

export const addMissedPageEvent = (path: string, url: string) => {
  missed_events.push({ event: { type: "page", path, url } })
}

export const trackSegmentPageEvent = (path: string, url: string): void => {
  if (typeof window === "undefined" || !window.analytics) {
    addMissedPageEvent(path, url)
    return console.warn(`Failed to track event: segment is undefined`)
  }

  return window.analytics.page({ path, url })
}

export const addMissedTrackEvent = (
  event: string,
  data: Record<string, any>
) => {
  missed_events.push({ event: { type: "track", event, data } })
}

export const trackSegmentEvent = (
  event: string,
  data: Record<string, any> = {}
): void => {
  if (typeof window === "undefined" || !window.analytics) {
    addMissedTrackEvent(event, data)
    return console.warn(`Failed to track event: segment is undefined`)
  }

  return window.analytics.track(event, data)
}

export const identifySegmentUser = (
  userId?: string,
  traits: Record<string, any> = {}
): void => {
  if (typeof window === "undefined" || !window.analytics) {
    return console.warn(`Failed to identify user: segment is undefined`)
  }

  return window.analytics.identify(userId, traits)
}

export const getCookie = (name: string) => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1]
}

export const isMobileDevice = () => {
  const conditions = [
    "Tablet",
    "Mobile",
    "Mobile Safari",
    "IEMobile/xyz",
    "Mobile/xyz",
    "Edge/ ",
  ]

  return conditions.some((condition) => navigator.userAgent.includes(condition))
}

export const trackAddToCart = (addedProduct: YocoCom.ConsolidatedCartItem) => {
  const cartId = fetchCartId()

  trackSegmentEvent("Product Added", {
    cart_id: cartId,
    product_id: addedProduct.id,
    sku: isConsolidatedProduct(addedProduct)
      ? addedProduct.sku
      : addedProduct.bundleCode,
    category: isConsolidatedProduct(addedProduct)
      ? addedProduct.type
      : "bundle",
    price: (Number(addedProduct.price) * addedProduct.quantity).toFixed(2),
    quantity: addedProduct.quantity,
  })
}

export const triggerSegmentIntegration = () => {
  if (!window.convert) {
    return
  }
  if (!window.analytics) {
    return
  }

  const data = getIntegrationData()
  for (const {
    experienceId,
    variationName,
    variationId,
    experienceName,
  } of data)
    window.analytics.track("Convert Event", {
      campaignId: experienceId,
      experimentName: experienceName,
      variationName: variationName,
      variationId: variationId,
    })
}

const getIntegrationData = () => {
  const data: any = []
  if (!window.convert) return data
  for (const experienceId in window.convert.currentData.experiences) {
    const variationId =
      window.convert.currentData.experiences[experienceId].variation.id
    const experienceName = anonymizeData(
      window.convert.data.experiences.find(
        ({ id }: { id: any }) => String(id) === String(experienceId)
      )?.name || "unknown experience name",
      experienceId
    )
    const variationName = anonymizeData(
      window.convert.currentData.experiences[experienceId].variation.name ||
        "unknown variation name",
      variationId
    )
    data.push({
      experienceId,
      experienceName,
      variationId,
      variationName,
    })
  }
  return data
}

const anonymizeData = (data: any, anonymized: any) => {
  if (!data || !window.convert) return ""
  if (window.convert.data.project.settings.data_anonymization) return anonymized
  return encodeURIComponent(data.replace(/[^a-zA-Z\-_.\s0-9]/g, "")).slice(
    0,
    40
  )
}
